import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {AlertPreviewChart} from 'app/components/Alerts/AlertPreviewChart';
import {DEFAULT_UPSTREAM_VEG_ALERT_POLICY} from 'app/components/Alerts/AlertsPoliciesPanel';
import {FinalizeEnrollmentAlertStep} from 'app/components/Alerts/Steps/FinalizeEnrollmentAlertStep';
import {useOrganizationOverlays, useProjectOverlays} from 'app/components/Library/OverlaysProvider';
import {
  AlertPolicy,
  ApiFeature,
  CategoryAlertProperties,
  ThresholdAlertProperties,
} from 'app/modules/Remote/Feature/types';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {getAreaUnit} from 'app/modules/Remote/Organization';
import * as C from 'app/utils/constants';
import {
  CUSTOM_ALERT_TYPE_CATEGORY,
  CUSTOM_ALERT_TYPE_COMPOSITE,
  CUSTOM_ALERT_TYPE_THRESHOLD,
  CUSTOM_ALERT_TYPE_VEGETATION_DROP,
  REGRID_FEATURE_COLLECTION_ID,
} from 'app/utils/constants';
import featureFlags, {useUserInfo} from 'app/utils/featureFlags';
import {alphanumericSort} from 'app/utils/featureUtils';
import {METAWRI_CANOPY_HEIGHT, NLCD_LANDCOVER} from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';

import {useAlertPolicies} from './AlertPolicyProvider';
import {AreaAlertStep} from './Steps/AreaAlertStep';
import {CompositeAlertStep} from './Steps/CompositeAlertStep';
import {LayerAlertStep} from './Steps/LayerAlertStep';
import {ThresholdAlertStep} from './Steps/ThresholdAlertStep';
import {TimeAlertStep} from './Steps/TimeAlertStep';
import cs from './styles.module.scss';
import {AlertFormState, useAlertForm} from './useAlertForm';

export interface AlertConfigurationModalProps {
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  allFeatures: ApiFeature[];
  selectedFeatureId: number;
  alertPolicy?: AlertPolicy;
  isOpen: boolean;
  onClose: () => void;
  canModifyAlertPolicies: boolean;
}

export const ALERT_ERROR_MESSAGES = {
  MISSING_NAME: 'Please name the Lookout policy',
  MISSING_LAYER: 'Please make a layer selection',
  DUPLICATE_NAME: 'Another Lookout policy exists with this name, these must be unique',
  MISSING_CATEGORIES: 'Please select From and To categories for your Lookout policy',
  ALERT_TYPE_CHANGE:
    'This dataset uses a different alert type. To use this dataset, please create a new lookout policy instead.',
};

export const AlertConfigurationModal: React.FC<AlertConfigurationModalProps> = ({
  featureCollection,
  allFeatures,
  alertPolicy,
  isOpen,
  onClose,
  selectedFeatureId,
  canModifyAlertPolicies,
}) => {
  const updateMode = !!alertPolicy;

  const {projectOverlays: allPojectOverlays, overlaySettings} =
    useProjectOverlays(featureCollection);

  const projectOverlays = allPojectOverlays
    // Filter out parcels from the list of overlays.
    .filter((o) => o.id !== REGRID_FEATURE_COLLECTION_ID)
    // Filter out overlays without the polygon type
    .filter((o) => ['POLYGON', 'MULTIPOLYGON'].includes(o.schema.geoType));

  const [organization, profile] = useUserInfo();

  const {data: organizationOverlayList} = useOrganizationOverlays(organization.get('id'));
  const organizationOverlays = React.useMemo(
    () =>
      (organizationOverlayList ?? []).map((overlay) => ({
        id: overlay.featureCollectionId,
        name: overlay.name,
      })),
    [organizationOverlayList]
  );

  const areaUnit = getAreaUnit(organization);
  const displayAreaUnit = areaUnit === C.UNIT_AREA_HECTARE ? C.UNIT_AREA_HECTARE : C.UNIT_AREA_ACRE;

  // Adapted from AnalyzePlygonPopupWithState, but we aren't checking whether
  // the individual features have data for each layerKey.
  const availableLayerKeys = React.useMemo(
    () => {
      return layerUtils
        .getLayerMenuOptionsForFeatureCollection(featureCollection)
        .filter((key) => {
          const layer = layerUtils.getLayer(key);
          const {filter: layerFilter} = layer;
          const filterByLayer = layerFilter ? layerFilter(featureCollection) : true;
          const rawLayerFilter = filterByLayer && layerUtils.getRawLayerKey(key);

          // NOTE: For now we are hardcoding out this layer. The lookouts backend currently
          // only supports layers using cogs and NLCD still uses tiles. This is the only relevant
          // layer on tiles and we don't have an easy generalized way for checking that here
          const isNLCD = key === NLCD_LANDCOVER;

          // NOTE: For now we are hardcoding out this layer. This data is high res and causes
          // OOM errors on the backend. It isn't going to be updated so as a workaround we just
          // won't allow lookouts to be created with it.
          const isMetaWRICanopyHeight = key == METAWRI_CANOPY_HEIGHT;

          return rawLayerFilter && !isNLCD && !isMetaWRICanopyHeight;
        })
        .sort((a, b) =>
          alphanumericSort(layerUtils.getLayer(a).display, layerUtils.getLayer(b).display)
        );
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // see AnalyzePolygonPopupWithState, ok to ignore as FeatureCollection experiences realtime updates
  );

  const {formState, dispatch, handleReset, handleClose} = useAlertForm({
    updateMode,
    alertPolicy,
    availableLayerKeys,
    onClose,
  });

  const {
    alertPolicies,
    actions: {
      createAlertPolicy,
      updateAlertPolicy,
      enrollFeaturesInAlertPolicy,
      unenrollFeaturesInAlertPolicy,
    },
    meta: {isFetching},
  } = useAlertPolicies();

  const alertPolicyNames = React.useMemo(
    () => alertPolicies.map((alertPolicy) => alertPolicy.name),
    [alertPolicies]
  );

  const featuresEnrolledInAlert: ApiFeature[] = React.useMemo(() => {
    if (!updateMode) return [];
    const enrolledFeatureIDs = alertPolicy.enrollments.map((enrollment) => enrollment.featureId);
    return allFeatures.filter((feature) => enrolledFeatureIDs.includes(feature?.id));
  }, [alertPolicy, updateMode, allFeatures]);

  const currentFeature: ApiFeature | undefined = React.useMemo(() => {
    return allFeatures.find((feature) => feature?.id === selectedFeatureId);
  }, [allFeatures, selectedFeatureId]);

  const validateNameIsNotDuplicate = React.useCallback(
    (name: string) => {
      return (
        (updateMode && alertPolicy?.name === name) ||
        !alertPolicyNames.includes(name) ||
        name === DEFAULT_UPSTREAM_VEG_ALERT_POLICY.name
      );
    },
    [alertPolicy?.name, alertPolicyNames, updateMode]
  );

  const compareFeatureIDLists = (
    previous: number[],
    current: number[]
  ): {
    inPreviousButNotCurrent: number[];
    inCurrentButNotPrevious: number[];
  } => {
    const previousDiff = previous.filter((id) => !current.includes(id));
    const currentDiff = current.filter((id) => !previous.includes(id));
    return {
      inPreviousButNotCurrent: previousDiff,
      inCurrentButNotPrevious: currentDiff,
    };
  };

  // Step-by-step validation functions
  const validateLayerStep = React.useCallback((formState: AlertFormState): boolean => {
    // For vegetation drop, layer is automatically set, so always valid
    if (formState.alertType === CUSTOM_ALERT_TYPE_VEGETATION_DROP) return true;
    return !!formState.layer;
  }, []);

  const validateAreaStep = React.useCallback((_: AlertFormState): boolean => {
    // All selections on this page are optional
    return true;
  }, []);
  const validateTimeStep = React.useCallback((formState: AlertFormState): boolean => {
    if (
      formState.alertType !== CUSTOM_ALERT_TYPE_THRESHOLD &&
      formState.alertType !== CUSTOM_ALERT_TYPE_COMPOSITE
    )
      return true;
    const numScenes = formState.properties.numConsecutiveScenes as number;
    return numScenes >= 1;
  }, []);

  const validateCompositeStep = React.useCallback((formState: AlertFormState): boolean => {
    if (formState.alertType !== CUSTOM_ALERT_TYPE_COMPOSITE) return true;
    const {groups} = formState.properties;
    // Mirrors the backend's own validators, so a policy the builder accepts is one the API accepts.
    return (
      groups.length > 0 &&
      groups.every(
        (group) =>
          group.length > 0 &&
          group.every(
            (condition) =>
              (condition.min !== null || condition.max !== null) &&
              // The backend allows min === max when both bounds are inclusive, which the builder
              // always writes.
              (condition.min === null || condition.max === null || condition.min <= condition.max)
          )
      )
    );
  }, []);

  const validateThresholdStep = React.useCallback((formState: AlertFormState): boolean => {
    if (formState.alertType === CUSTOM_ALERT_TYPE_THRESHOLD) {
      const thresholdProps = formState.properties as unknown as ThresholdAlertProperties;
      return (
        thresholdProps.value !== undefined &&
        thresholdProps.value !== null &&
        thresholdProps.direction !== undefined &&
        thresholdProps.direction !== null
      );
    } else if (formState.alertType === CUSTOM_ALERT_TYPE_CATEGORY) {
      const categoryProps = formState.properties as unknown as CategoryAlertProperties;
      return categoryProps.fromCategories.length > 0 && categoryProps.toCategories.length > 0;
    } else if (formState.alertType === CUSTOM_ALERT_TYPE_VEGETATION_DROP) {
      // Vegetation drop doesn't use threshold step, so validation always passes
      return true;
    }
    return false;
  }, []);

  const validateFinalizeStep = React.useCallback(
    (formState: AlertFormState): boolean => {
      return !!formState.name.trim() && validateNameIsNotDuplicate(formState.name);
    },
    [validateNameIsNotDuplicate]
  );

  const validateAlertPolicy = React.useCallback(
    (formState: AlertFormState): [false, string] | [true, null] => {
      // Per-step validators
      const validateCategoriesSelected = (formState: AlertFormState): boolean => {
        if (formState.alertType !== CUSTOM_ALERT_TYPE_CATEGORY) return true;
        return (
          !!formState.properties.toCategories.length && !!formState.properties.fromCategories.length
        );
      };

      const validateAlertTypeNotChanged = (formState: AlertFormState): boolean => {
        // Only applies in update mode
        if (!updateMode || !alertPolicy) return true;

        // Check if alert type has changed
        return formState.alertType === alertPolicy.alertType;
      };

      const validateNamePresent = (formState: AlertFormState): boolean => !!formState.name;
      const validateLayerPresent = (formState: AlertFormState): boolean => !!formState.layer;

      const validations: [boolean, string][] = [
        [validateNamePresent(formState), ALERT_ERROR_MESSAGES.MISSING_NAME],
        [validateNameIsNotDuplicate(formState.name), ALERT_ERROR_MESSAGES.DUPLICATE_NAME],
        [validateLayerPresent(formState), ALERT_ERROR_MESSAGES.MISSING_LAYER],
        [validateCategoriesSelected(formState), ALERT_ERROR_MESSAGES.MISSING_CATEGORIES],
        [validateAlertTypeNotChanged(formState), ALERT_ERROR_MESSAGES.ALERT_TYPE_CHANGE],
      ];

      for (const [validation, error] of validations) {
        if (!validation) {
          return [false, error];
        }
      }
      return [true, null];
    },
    [alertPolicy, updateMode, validateNameIsNotDuplicate]
  );

  const handleEnrollmentUpdates = async (alertPolicy: AlertPolicy, formState: AlertFormState) => {
    const updatedAlertPolicy = await updateAlertPolicy(alertPolicy, formState);
    const {inPreviousButNotCurrent, inCurrentButNotPrevious} = compareFeatureIDLists(
      alertPolicy.enrollments.map((enrollment) => enrollment.featureId),
      formState.enrolledIds
    );
    const updatedPolicyWithEnrollments = {
      ...updatedAlertPolicy,
      enrollments: alertPolicy.enrollments,
    } as AlertPolicy;
    const arraysAreEqual =
      inPreviousButNotCurrent.length === 0 && inCurrentButNotPrevious.length === 0;
    if (arraysAreEqual) return;
    if (inCurrentButNotPrevious.length)
      await enrollFeaturesInAlertPolicy(
        updatedPolicyWithEnrollments,
        inCurrentButNotPrevious,
        true
      );
    if (inPreviousButNotCurrent.length)
      await unenrollFeaturesInAlertPolicy(updatedPolicyWithEnrollments, inPreviousButNotCurrent);
  };

  const submitForm = async () => {
    const [isValid, error] = validateAlertPolicy(formState);
    if (!isValid) console.error(error); // User should not hit this case with current validations.
    if (updateMode) {
      await handleEnrollmentUpdates(alertPolicy, formState);
    } else {
      const newAlertPolicy = await createAlertPolicy(featureCollection, formState);
      if (formState.enrolledIds.length) {
        await enrollFeaturesInAlertPolicy(
          {...(newAlertPolicy as AlertPolicy), enrollments: []},
          formState.enrolledIds,
          true
        );
      }
    }
    handleClose();
  };

  const isVegetationDrop = formState.alertType === CUSTOM_ALERT_TYPE_VEGETATION_DROP;
  const isComposite = formState.alertType === CUSTOM_ALERT_TYPE_COMPOSITE;
  const isThresholdAlert = formState.alertType === CUSTOM_ALERT_TYPE_THRESHOLD;
  const showCompositeOption = featureFlags.COMPOSITE_LOOKOUTS(organization, profile);

  const compositeStep = {
    id: 'composite-alert',
    title: 'Set conditions',
    panel: (
      <CompositeAlertStep
        disableInputs={!canModifyAlertPolicies}
        formState={formState}
        dispatch={dispatch}
        feature={currentFeature}
        featureCollection={featureCollection}
        availableLayerKeys={availableLayerKeys}
      />
    ),
    nextButtonProps: {
      disabled: !validateCompositeStep(formState),
    } as B.ButtonProps,
  };

  const steps: {
    id: string;
    title: string;
    panel: React.ReactElement;
    nextButtonProps?: B.ButtonProps;
  }[] = [
    {
      id: 'layer-alert',
      title: 'Choose dataset',
      panel: (
        <LayerAlertStep
          disableInputs={!canModifyAlertPolicies}
          formState={formState}
          dispatch={dispatch}
          updateMode={updateMode}
          alertPolicy={alertPolicy}
          availableLayerKeys={availableLayerKeys}
          showCompositeOption={showCompositeOption}
        />
      ),
      nextButtonProps: {
        disabled: !validateLayerStep(formState),
      } as B.ButtonProps,
    },
    {
      id: 'where-alert',
      title: 'Set spatial rules',
      panel: (
        <AreaAlertStep
          disableInputs={!canModifyAlertPolicies}
          formState={formState}
          dispatch={dispatch}
          projectOverlays={projectOverlays}
          organizationOverlays={organizationOverlays}
          overlaySettings={overlaySettings}
          displayAreaUnit={displayAreaUnit}
        />
      ),
      nextButtonProps: {
        disabled: !validateAreaStep(formState),
      } as B.ButtonProps,
    },
    {
      id: 'when-alert',
      title: 'Set temporal rules',
      panel: (
        <TimeAlertStep
          disableInputs={!canModifyAlertPolicies}
          formState={formState}
          dispatch={dispatch}
        />
      ),
      nextButtonProps: {
        disabled: !validateTimeStep(formState),
      } as B.ButtonProps,
    },
    // Spatial and temporal rules only read formState.alertType, not the condition groups, so
    // conditions can come after them without a data dependency.
    ...(isComposite ? [compositeStep] : []),
    // Skip the threshold step for vegetation drop alerts, and for composites, which set their
    // conditions in the step above instead.
    ...(isVegetationDrop || isComposite
      ? []
      : [
          {
            id: 'threshold-alert',
            title: 'Set threshold',
            panel: (
              <ThresholdAlertStep
                disableInputs={!canModifyAlertPolicies}
                formState={formState}
                dispatch={dispatch}
                feature={currentFeature}
                featureCollection={featureCollection}
              />
            ),
            nextButtonProps: {
              disabled: !validateThresholdStep(formState),
            } as B.ButtonProps,
          },
        ]),
    {
      id: 'enroll-properties',
      title: 'Enroll and finalize',
      panel: (
        <FinalizeEnrollmentAlertStep
          allFeatures={allFeatures}
          dispatch={dispatch}
          formEnrolledIds={formState.enrolledIds}
          initialSelection={
            updateMode ? featuresEnrolledInAlert : currentFeature ? [currentFeature] : []
          }
          previousIds={alertPolicy?.enrollments.map((enrollment) => enrollment.featureId) || []}
          currentIds={formState.enrolledIds}
          formState={formState}
          validateNameIsNotDuplicate={validateNameIsNotDuplicate}
          disableInputs={!canModifyAlertPolicies}
          projectOverlays={projectOverlays}
          organizationOverlays={organizationOverlays}
          displayAreaUnit={displayAreaUnit}
        />
      ),
    },
  ];

  // if we have no available layers just exit here.
  if (!availableLayerKeys.length)
    return (
      <B.Dialog isOpen={isOpen} onClose={onClose} title="No available layers">
        <B.DialogBody>
          No available layers found. Please add an analytical layer (e.g. S2 Vegetation) to your
          property.
        </B.DialogBody>
      </B.Dialog>
    );

  return (
    <>
      <B.MultistepDialog
        className={cs.alertConfigurationModal}
        isOpen={isOpen}
        title={updateMode ? `Update Lookout policy: ${formState.name}` : 'Create Lookout policy'}
        finalButtonProps={{
          disabled: !validateFinalizeStep(formState),
          text: updateMode ? 'Submit updates' : 'Finish',
          onClick: submitForm,
          loading: isFetching,
        }}
        onClose={handleClose}
        onOpening={handleReset}
        canEscapeKeyClose={!isFetching}
        backButtonProps={{
          text: 'Back',
        }}
        canOutsideClickClose={false}
      >
        {steps.map((s) => (
          <B.DialogStep
            key={s.id}
            id={s.id}
            title={s.title}
            panel={s.panel}
            nextButtonProps={s.nextButtonProps}
          />
        ))}
      </B.MultistepDialog>
      {/* Hidden AlertPreviewChart to prefetch images!
       Starts fetching when layer is selected. These will be cached and
       ready likely by the time the user gets to the threshold step
       and sees the chart. Caching, canceling requests, etc should
       all be handled by the RasterCalculationStore. */}
      {formState.layer && currentFeature && (isComposite || isThresholdAlert) && (
        <div style={{display: 'none'}}>
          <AlertPreviewChart
            feature={currentFeature}
            layer={formState.layer}
            featureCollection={featureCollection}
          />
        </div>
      )}
    </>
  );
};
